import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Wrench, ArrowLeft, Star, Calendar, MapPin, Phone, Edit, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const WorkerDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [categories, setCategories] = useState([]);
  const [profileData, setProfileData] = useState({
    category: '',
    location: '',
    hourly_rate: '',
    experience_years: '',
    bio: '',
    skills: ''
  });

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);

  const fetchData = async () => {
    try {
      const [jobsRes] = await Promise.all([
        axios.get(`${API}/jobs/worker`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setJobs(jobsRes.data);

      const workersRes = await axios.get(`${API}/workers`);
      const myProfile = workersRes.data.find(w => w.email === user.email);
      setProfile(myProfile);
      
      if (myProfile) {
        setProfileData({
          category: myProfile.category,
          location: myProfile.location,
          hourly_rate: myProfile.hourly_rate.toString(),
          experience_years: myProfile.experience_years.toString(),
          bio: myProfile.bio,
          skills: myProfile.skills.join(', ')
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleProfileSubmit = async () => {
    if (!profileData.category || !profileData.location || !profileData.hourly_rate || 
        !profileData.experience_years || !profileData.bio || !profileData.skills) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const payload = {
        ...profileData,
        hourly_rate: parseFloat(profileData.hourly_rate),
        experience_years: parseInt(profileData.experience_years),
        skills: profileData.skills.split(',').map(s => s.trim()).filter(s => s)
      };

      const method = profile ? 'put' : 'post';
      await axios[method](
        `${API}/workers/profile`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Profile ${profile ? 'updated' : 'created'} successfully!`);
      setShowProfileEditor(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Header */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              data-testid="back-to-home-worker-btn"
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-[#003B5C] hover:text-[#002A42] transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              <Wrench className="w-8 h-8" />
              <span className="text-2xl font-bold font-['Outfit']">SkillYaad</span>
            </button>

            <div className="flex items-center space-x-4">
              <span className="text-[#475569]">Hello, <span className="font-semibold text-[#003B5C]">{user?.name}</span></span>
              <Button
                data-testid="logout-worker-btn"
                onClick={logout}
                variant="outline"
                className="border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C] hover:text-white rounded-full"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#003B5C] mb-2">Worker Dashboard</h1>
          <p className="text-[#475569]">Manage your profile and job requests</p>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 border-4 border-[#003B5C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#475569]">Loading...</p>
          </div>
        ) : (
          <>
            {/* Profile Section */}
            <div className="bg-white rounded-xl shadow-sm p-8 mb-8" data-testid="worker-profile-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#0F172A]">My Profile</h2>
                <div className="flex gap-2">
                  <Button
                    data-testid="verification-btn"
                    onClick={() => navigate('/verification')}
                    variant="outline"
                    className="border-[#009B4D] text-[#009B4D] hover:bg-[#009B4D] hover:text-white rounded-full font-bold"
                  >
                    {profile?.verified ? 'Verification Status' : 'Get Verified'}
                  </Button>
                  <Button
                    data-testid="edit-profile-btn"
                    onClick={() => setShowProfileEditor(true)}
                    className="bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full font-bold"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    {profile ? 'Edit Profile' : 'Create Profile'}
                  </Button>
                </div>
              </div>

              {profile ? (
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-[#94A3B8] block mb-1">Category</label>
                      <p className="text-[#0F172A] font-semibold">{profile.category}</p>
                    </div>
                    <div>
                      <label className="text-sm text-[#94A3B8] block mb-1">Location</label>
                      <p className="text-[#0F172A] font-semibold flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {profile.location}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-[#94A3B8] block mb-1">Hourly Rate</label>
                      <p className="text-2xl text-[#003B5C] font-bold font-['Space_Grotesk']">
                        ${profile.hourly_rate}/hour
                      </p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-[#94A3B8] block mb-1">Experience</label>
                      <p className="text-[#0F172A] font-semibold flex items-center">
                        <Briefcase className="w-4 h-4 mr-1" />
                        {profile.experience_years} years
                      </p>
                    </div>
                    <div>
                      <label className="text-sm text-[#94A3B8] block mb-1">Rating</label>
                      <div className="flex items-center space-x-2">
                        <Star className="w-5 h-5 text-[#FFB81C] fill-[#FFB81C]" />
                        <span className="text-xl font-bold text-[#0F172A]">{profile.rating.toFixed(1)}</span>
                        <span className="text-[#94A3B8]">({profile.review_count} reviews)</span>
                      </div>
                    </div>
                    <div>
                      <Button
                        data-testid="view-public-profile-btn"
                        onClick={() => navigate(`/workers/${profile.id}`)}
                        variant="outline"
                        className="border-[#003B5C] text-[#003B5C] hover:bg-[#003B5C] hover:text-white rounded-full"
                      >
                        View Public Profile
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-[#94A3B8] mb-4">You haven't created your worker profile yet</p>
                  <Button
                    data-testid="create-profile-cta-btn"
                    onClick={() => setShowProfileEditor(true)}
                    className="bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full px-6 py-2 font-bold"
                  >
                    Create Profile Now
                  </Button>
                </div>
              )}
            </div>

            {/* Job Requests */}
            <div className="bg-white rounded-xl shadow-sm p-8" data-testid="worker-jobs-list">
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6">Job Requests</h2>
              {jobs.length === 0 ? (
                <p className="text-[#94A3B8] text-center py-8">No job requests yet</p>
              ) : (
                <div className="space-y-4">
                  {jobs.map((job) => (
                    <div
                      key={job.id}
                      data-testid={`worker-job-card-${job.id}`}
                      className="border border-gray-200 rounded-xl p-6 hover:border-[#FFB81C]/50 transition-all duration-300"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-[#0F172A]">{job.customer_name}</h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              job.status === 'pending' ? 'bg-[#FFB81C]/20 text-[#FFB81C]' :
                              job.status === 'completed' ? 'bg-[#009B4D]/20 text-[#009B4D]' :
                              'bg-gray-200 text-gray-700'
                            }`}>
                              {job.status.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-[#475569] mb-3">{job.description}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                            <div className="flex items-center">
                              <Calendar className="w-4 h-4 mr-1" />
                              {new Date(job.preferred_date).toLocaleDateString()}
                            </div>
                            <div className="flex items-center">
                              <MapPin className="w-4 h-4 mr-1" />
                              {job.location}
                            </div>
                            {job.customer_phone && (
                              <div className="flex items-center">
                                <Phone className="w-4 h-4 mr-1" />
                                {job.customer_phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Profile Editor Modal */}
      <Dialog open={showProfileEditor} onOpenChange={setShowProfileEditor}>
        <DialogContent data-testid="profile-editor-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#003B5C]">
              {profile ? 'Edit' : 'Create'} Your Profile
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Category</label>
              <Select value={profileData.category} onValueChange={(val) => setProfileData({ ...profileData, category: val })}>
                <SelectTrigger data-testid="profile-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Location</label>
              <Input
                data-testid="profile-location-input"
                value={profileData.location}
                onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                placeholder="e.g., Kingston, Jamaica"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Hourly Rate ($)</label>
                <Input
                  data-testid="profile-hourly-rate-input"
                  type="number"
                  value={profileData.hourly_rate}
                  onChange={(e) => setProfileData({ ...profileData, hourly_rate: e.target.value })}
                  placeholder="50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Years of Experience</label>
                <Input
                  data-testid="profile-experience-input"
                  type="number"
                  value={profileData.experience_years}
                  onChange={(e) => setProfileData({ ...profileData, experience_years: e.target.value })}
                  placeholder="5"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Bio</label>
              <Textarea
                data-testid="profile-bio-input"
                value={profileData.bio}
                onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                placeholder="Tell customers about your experience and expertise..."
                className="min-h-[100px]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Skills (comma-separated)</label>
              <Input
                data-testid="profile-skills-input"
                value={profileData.skills}
                onChange={(e) => setProfileData({ ...profileData, skills: e.target.value })}
                placeholder="e.g., Engine Repair, Diagnostics, Brake Systems"
              />
            </div>
            <Button
              data-testid="save-profile-btn"
              onClick={handleProfileSubmit}
              className="w-full bg-[#FFB81C] hover:bg-[#E5A000] text-[#003B5C] rounded-full py-3 font-bold"
            >
              {profile ? 'Update' : 'Create'} Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerDashboard;